import { useState } from "react";
import type { GalaxyState } from "../../types/galaxy";
import { FACTION_COLORS } from "../../types/galaxy";

type FeedTab = "news" | "missions" | "intel";

interface Props {
  galaxyState: GalaxyState;
  onSystemClick: (systemId: string) => void;
  onSetCourse: (systemId: string) => void;
}

export function NewsFeed({ galaxyState, onSystemClick, onSetCourse }: Props) {
  const [activeTab, setActiveTab] = useState<FeedTab>("news");

  const tabs: { id: FeedTab; label: string }[] = [
    { id: "news", label: "NEWS" },
    { id: "missions", label: "MISSIONS" },
    { id: "intel", label: "INTEL" },
  ];

  return (
    <div className="news-feed">
      <div className="nf-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`nf-tab ${activeTab === tab.id ? "nf-tab-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="nf-content">
        {activeTab === "news" && (
          <NewsTab galaxyState={galaxyState} onSystemClick={onSystemClick} />
        )}
        {activeTab === "missions" && (
          <MissionsTab galaxyState={galaxyState} onSystemClick={onSystemClick} onSetCourse={onSetCourse} />
        )}
        {activeTab === "intel" && (
          <IntelTab galaxyState={galaxyState} onSystemClick={onSystemClick} />
        )}
      </div>
    </div>
  );
}

function NewsTab({ galaxyState, onSystemClick }: { galaxyState: GalaxyState; onSystemClick: (id: string) => void }) {
  const events = galaxyState.recentEvents
    .filter(e => e.visibility === "public")
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const systemMap = new Map(galaxyState.systems.map(s => [s.id, s]));

  return (
    <div className="nf-list">
      {events.map(event => {
        const sys = systemMap.get(event.systemId);
        return (
          <div key={event.id} className="nf-item" onClick={() => onSystemClick(event.systemId)}>
            <div className="nf-item-header">
              <span className="nf-time">{formatRelativeTime(event.timestamp)}</span>
              {sys && <span className="nf-system">{sys.name}</span>}
            </div>
            <div className="nf-headline">{event.headline}</div>
            <div className="nf-description">{event.description}</div>
            {event.factionsInvolved.length > 0 && (
              <div className="nf-factions">
                {event.factionsInvolved.map(f => (
                  <span
                    key={f}
                    className="nf-faction-tag"
                    style={{ borderColor: FACTION_COLORS[f] ?? "#666", color: FACTION_COLORS[f] ?? "#666" }}
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MissionsTab({
  galaxyState,
  onSystemClick,
  onSetCourse,
}: {
  galaxyState: GalaxyState;
  onSystemClick: (id: string) => void;
  onSetCourse: (id: string) => void;
}) {
  const missions = galaxyState.availableMissions;
  const systemMap = new Map(galaxyState.systems.map(s => [s.id, s]));

  return (
    <div className="nf-list">
      {missions.map(mission => {
        const sys = systemMap.get(mission.systemId);
        return (
          <div key={mission.id} className="nf-item nf-mission-item">
            <div className="nf-item-header">
              <span className={`nf-urgency nf-urgency-${mission.urgency}`}>
                {mission.urgency}
              </span>
              <span className="nf-mission-type">{mission.type}</span>
              {sys && (
                <span
                  className="nf-system nf-system-link"
                  onClick={(e) => { e.stopPropagation(); onSystemClick(mission.systemId); }}
                >
                  {sys.name}
                </span>
              )}
            </div>
            <div className="nf-headline">{mission.title}</div>
            <div className="nf-description">{mission.description}</div>
            <div className="nf-mission-actions">
              <button
                className="nf-action-btn"
                onClick={(e) => { e.stopPropagation(); onSystemClick(mission.systemId); }}
              >
                VIEW ON MAP
              </button>
              <button
                className="nf-action-btn nf-action-primary"
                onClick={(e) => { e.stopPropagation(); onSetCourse(mission.systemId); }}
              >
                SET COURSE
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IntelTab({ galaxyState, onSystemClick }: { galaxyState: GalaxyState; onSystemClick: (id: string) => void }) {
  const intel = galaxyState.recentEvents
    .filter(e => e.visibility === "faction_only" && e.faction === galaxyState.player.faction)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const systemMap = new Map(galaxyState.systems.map(s => [s.id, s]));

  if (intel.length === 0) {
    return (
      <div className="nf-empty">
        No intelligence reports available.
      </div>
    );
  }

  return (
    <div className="nf-list">
      {intel.map(event => {
        const sys = systemMap.get(event.systemId);
        return (
          <div key={event.id} className="nf-item nf-intel-item" onClick={() => onSystemClick(event.systemId)}>
            <div className="nf-item-header">
              <span className="nf-time">{formatRelativeTime(event.timestamp)}</span>
              {sys && <span className="nf-system">{sys.name}</span>}
              <span className="nf-classified">CLASSIFIED</span>
            </div>
            <div className="nf-headline">{event.headline}</div>
            <div className="nf-description">{event.description}</div>
          </div>
        );
      })}
    </div>
  );
}

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
